import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "user/components/ui/sheet";
import Header from "user/components/common/Header";
import Checkbox from "user/components/form/Checkbox";
import OrderDetailItems from "./-components/OrderDetailItems";
import { SimpleOrderItem } from "./-components/OrderItem";
import { useGetOrderInfo } from "common/hooks/user/order/useGetOrderInfo";
import { enum_api_uri } from "common/hooks/utils/enum";
import { usePutOrderCancel } from "common/hooks/user/order/usePutOrderCancel";
import { errorMsgHandler } from "common/hooks/utils/utils";
import { SystemPopup, ViewLargeImg } from "user/components/common/Overlay";
import useOverlay, { OverlayWrapper } from "common/hooks/user/useOverlay";
import { useGetCommonCancel } from "common/hooks/common/useGetCommonCancel";
import { TextareaField } from "user/components/common/InputField";
import { useAuthStore } from "user/store/useAuthStore";
import { ReactComponent as SelectIcon } from "../../../common/assets/icons/selectIcon.svg";
import axios from "axios";
import Spinner from "common/components/spinner";

const dashedStyleLine =
    "inline-block w-[2px] h-full bg-[linear-gradient(0deg,transparent_0px,transparent_3px,#E5E5F1_3px,#E5E5F1_9px,transparent_9px,transparent_15px,#E5E5F1_15px,#E5E5F1_21px,transparent_21px,transparent_27px,#E5E5F1_27px,#E5E5F1_33px,transparent_33px,transparent_39px,#E5E5F1_39px,#E5E5F1_45px,transparent_45px,transparent_51px,#E5E5F1_51px,#E5E5F1_57px,transparent_57px)]";

const CancelNote = ({ className }: { className?: string }) => {
    const style = twMerge("flex flex-col gap-[4px] mt-[8px]", className);
    return (
        <ul className={style}>
            <li className="grid grid-cols-[100px_1fr] py-[4px]">
                <span className="font-[500]">미확인</span>
                <span>주문 취소 가능</span>
            </li>
            <li className="grid grid-cols-[100px_1fr] py-[4px]">
                <span className="font-[500]">주문접수</span>
                <span className="break-keep">접수완료</span>
            </li>
            <li className="grid grid-cols-[100px_1fr] py-[4px]">
                <span className="font-[500]">준비중</span>
                <span className="break-keep">준비완료</span>
            </li>
            <li className="grid grid-cols-[100px_1fr] py-[4px]">
                <span className="font-[500]">배송완료</span>
                <p className="break-keep">
                    고객센터로 연락 후 수거(취소) <br />
                    <span className="text-[#DC2626]">환불 불가</span>
                </p>
            </li>
        </ul>
    );
};

const initialValues = {
    reason_idx: null,
    reason_detail: "",
    agree: false,
};

export default function OrderDetails({ isMember }: { isMember: boolean }) {
    const navigate = useNavigate();
    const params = useParams();
    const m_category = useAuthStore.getState().m_category;
    const orderIdx = params.idx;
    const [viewNote, setViewNote] = useState(false);
    const [cancel, setCancel] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sheetTriggerRef = useRef<HTMLButtonElement | null>(null);
    const cancelSheetTriggerRef = useRef<HTMLButtonElement | null>(null);
    const { overlay, openOverlay, closeOverlay } = useOverlay();
    const { data, isLoading, isError, error } = useGetOrderInfo(Number(orderIdx));
    const { mutate: cancelMutate } = usePutOrderCancel();
    const { data: cancelData } = useGetCommonCancel(false, !!m_category);

    const product_info = data?.product_info;
    const recipient_info = data?.recipient_info;
    const orderStatus = data?.status_info.order_status;
    // 주문 상태 코드 (1: 미확인, 2: 접수완료, 3: 준비완료, 4: 배송완료, 5: 주문취소, 6: 보류, 7: 삭제)
    const orderNotConfirmed = orderStatus === 1;
    const orderConfirmed = orderStatus === 2;
    const shippingReady = orderStatus === 3;
    const orderDeliveryCompleted = orderStatus === 4;
    const orderCancel = orderStatus === 5;
    const shippingOnHold = orderStatus === 6;

    // console.log(data);
    // console.log(product_info);

    useEffect(() => {
        if (isError && error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                const messageFromBackend = error.response?.data?.message;

                openOverlay(
                    <SystemPopup
                        message={messageFromBackend}
                        onClickClose={() => {
                            navigate(-1);
                            closeOverlay();
                        }}
                    />,
                );
            }
        }
    }, [isError, error]);

    useEffect(() => {
        if (data) {
            containerRef.current?.scrollIntoView();
        }
    }, [data]);

    return (
        <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
            {isLoading && <Spinner />}

            <div ref={containerRef} className="min-h-[100dvh] flex flex-col relative">
                {isMember ? (
                    <Header
                        goBack
                        // state 넘기기 위해 naviate 절대 경로로 넣어줌
                        onClickGoBack={() => navigate(`/orders`, { state: {} })}
                        onClickClose={() => navigate("/", { state: {} })}
                        title="주문상세"
                        className="sticky top-0 bg-white"
                    />
                ) : (
                    <Header title="주문상세" />
                )}
                <div className="wrapper py-[16px]">
                    <div className="flex gap-[8px] items-center">
                        {orderConfirmed && <span className="text-[#4B632B] text-[18px] leading-[28px] font-bold">접수완료</span>}
                        {shippingReady && <span className="text-[#4B632B] text-[18px] leading-[28px] font-bold">준비완료</span>}
                        {orderDeliveryCompleted && <span className="text-[#4B632B] text-[18px] leading-[28px] font-bold">배송완료</span>}
                        {orderNotConfirmed && <span className="text-[#484964] text-[18px] leading-[28px] font-bold">미확인</span>}
                        {orderCancel && <span className="text-[#DC2626] text-[18px] leading-[28px] font-bold">주문취소</span>}
                        {shippingOnHold && <span className="text-[#DC2626] text-[18px] leading-[28px] font-bold">보류</span>}

                        <span className="text-[18px] leading-[28px]">{data?.order_number}</span>
                        <span className="text-[14px] leading-[20px] text-[#999]">주문 {data?.order_date}</span>
                    </div>

                    <SimpleOrderItem
                        className="mt-[12px] mb-[16px]"
                        category={product_info?.product_category}
                        name={product_info?.product_name}
                        price={product_info?.product_amount}
                        quantity={product_info?.product_quantity}
                        totalPrice={
                            product_info
                                ? product_info.sales_amount || product_info.product_amount * product_info.product_quantity
                                : undefined
                        }
                        img={
                            product_info?.product_image?.file_name ? `${enum_api_uri.api_uri}/${product_info.product_image.file_name}` : ""
                        }
                    />

                    {(orderConfirmed || shippingReady || orderDeliveryCompleted) && (
                        <ul className="bg-[#F3F3F8] rounded-[12px] p-[12px] flex flex-col gap-[8px]">
                            {orderStatus < 5 && orderStatus >= 4 && (
                                <li>
                                    <div className="flex items-center gap-[8px] mb-[4px]">
                                        <span className="rounded-full w-[8px] h-[8px] bg-[#273219]"></span>
                                        <p className="text-[#484964] text-[14px] leading-[20px]">배송완료 {data?.delivery_complete_date}</p>
                                    </div>
                                    {product_info && product_info?.delivered_image.length > 0 && (
                                        <div className="flex gap-[4px]">
                                            <div className="px-[3px] h-[60px]">
                                                <span className={dashedStyleLine}></span>
                                            </div>
                                            <div className="flex gap-[4px]">
                                                {product_info.delivered_image.map(img => {
                                                    const src = enum_api_uri.api_uri + "/" + img.file_name;
                                                    return (
                                                        <figure
                                                            key={img.file_idx}
                                                            onClick={() =>
                                                                openOverlay(<ViewLargeImg src={src} closeOverlay={closeOverlay} />)
                                                            }
                                                        >
                                                            <img
                                                                src={src}
                                                                alt=""
                                                                className="w-[60px] h-[60px] rounded-[8px] overflow-hidden"
                                                            />
                                                        </figure>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </li>
                            )}
                            {orderStatus < 5 && orderStatus >= 3 && (
                                <li>
                                    <div className="flex items-center gap-[8px] mb-[4px]">
                                        <span className="rounded-full w-[8px] h-[8px] bg-[#A3AED0]"></span>
                                        <p className="text-[#484964] text-[14px] leading-[20px]">준비완료 {data?.ready_date}</p>
                                    </div>
                                    {product_info && product_info?.ready_image.length > 0 && (
                                        <div className="flex gap-[4px]">
                                            <div className="px-[3px] h-[60px]">
                                                <span className={dashedStyleLine}></span>
                                            </div>
                                            <div className="flex gap-[4px]">
                                                {product_info.ready_image.map(img => {
                                                    const src = enum_api_uri.api_uri + "/" + img.file_name;
                                                    return (
                                                        <figure
                                                            key={img.file_idx}
                                                            onClick={() =>
                                                                openOverlay(<ViewLargeImg src={src} closeOverlay={closeOverlay} />)
                                                            }
                                                        >
                                                            <img
                                                                src={src}
                                                                alt=""
                                                                className="w-[60px] h-[60px] rounded-[8px] overflow-hidden"
                                                            />
                                                        </figure>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </li>
                            )}
                            {orderStatus < 5 && orderStatus >= 2 && (
                                <li>
                                    <div className="flex items-center gap-[8px] mb-[4px]">
                                        <span className="rounded-full w-[8px] h-[8px] bg-[#A3AED0]"></span>
                                        <p className="text-[#484964] text-[14px] leading-[20px]">접수완료 {data?.accept_date}</p>
                                    </div>
                                </li>
                            )}
                        </ul>
                    )}
                    {isMember && orderNotConfirmed && (
                        <div className="flex rounded-[12px] border border-[#ddd] items-center">
                            <Link to={`/orders/${orderIdx}/update`} className="flex-1 text-[#484964] py-[12px] text-center">
                                접수내역 수정
                            </Link>

                            <span className="w-[1px] h-[16px] bg-[#ddd]"></span>
                            <button type="button" className="flex-1 text-[#484964] py-[12px]" onClick={() => setCancel(true)}>
                                주문취소
                            </button>
                        </div>
                    )}
                    {isMember && orderCancel && (
                        <ul className="bg-[#F3F3F8] rounded-[12px] p-[12px] flex flex-col gap-[8px]">
                            <li>
                                <div className="flex items-center gap-[8px] mb-[4px]">
                                    <span className="rounded-full w-[8px] h-[8px] bg-[#273219]"></span>
                                    <p className="text-[#484964] text-[14px] leading-[20px]">주문취소 {data?.cancel_date}</p>
                                </div>
                                <ul className="flex flex-col gap-[4px]">
                                    <li className="grid grid-cols-[100px_1fr] py-[4px]">
                                        <span className="text-[#999] font-[500]">취소사유</span>
                                        <span>
                                            {
                                                cancelData?.data.find(
                                                    item => item.cancel_reason_idx === data?.status_info.cancel_reason_idx,
                                                )?.cancel_reason
                                            }
                                        </span>
                                    </li>
                                    <li className="grid grid-cols-[100px_1fr] py-[4px]">
                                        <span className="text-[#999] font-[500]">상세사유</span>
                                        <span className="break-all">{data?.status_info.cancel_reason}</span>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    )}
                </div>
                {isMember && (orderConfirmed || shippingReady || orderDeliveryCompleted) && (
                    <div className="wrapper border-t-[8px] border-[#f3f3f8] bg-[#F8F9F7] flex flex-col gap-[8px] items-center py-[16px]">
                        <p className="text-[#4B632B] break-keep text-center">
                            현재 상태에선 주문을 취소하실 수 없습니다. <br /> 수정 및 취소가 필요하신 경우 <br /> 고객센터로 연락
                            부탁드립니다.
                        </p>
                        <div className="flex items-center text-[#4B632B]">
                            <a href="tel:027047880" className="flex-1 font-bold text-center whitespace-nowrap">
                                02-704-7880
                            </a>
                            <span className="w-[1px] h-[12px] bg-[#4B632B] mx-[10px]"></span>
                            <a href="tel:01077117880" className="flex-1 font-bold text-center whitespace-nowrap">
                                010-7711-7880
                            </a>
                        </div>
                        <button
                            type="button"
                            className="border border-[#ddd] rounded-[8px] text-[#484964] text-[14px] leading-[20px] bg-white px-[8px] py-[6px]"
                            onClick={() => setViewNote(true)}
                        >
                            주문취소 유의사항 확인
                        </button>
                    </div>
                )}

                {!isMember && (
                    <div className="wrapper border-t-[8px] border-[#f3f3f8] bg-[#F8F9F7] flex flex-col gap-[8px] items-center py-[16px]">
                        <p className="text-[#4B632B] break-keep text-center">
                            주문 상태가 미확인일 때 회원일 경우,
                            <br /> 주문내역을 수정 및 취소하실 수 있습니다. <br /> 비회원인 경우 고객센터로 연락 주세요.
                        </p>
                        <div className="flex items-center text-[#4B632B] justify-between w-full max-w-[250px] ">
                            <p className="font-bold text-center">02-704-7880</p>
                            <span className="w-[1px] h-[12px] bg-[#4B632B]"></span>
                            <p className="font-bold text-center">010-7711-7880</p>
                        </div>
                        <button
                            type="button"
                            className="border border-[#ddd] rounded-[8px] text-[#484964] text-[14px] leading-[20px] bg-white px-[8px] py-[6px]"
                            onClick={() => setViewNote(true)}
                        >
                            주문취소 유의사항 확인
                        </button>
                    </div>
                )}

                <OrderDetailItems
                    values={recipient_info}
                    orderDateFull={data?.order_date_full}
                    openOverlay={openOverlay}
                    closeOverlay={closeOverlay}
                />

                {isMember && (
                    <div className="wrapper border-t-[8px] border-[#f3f3f8] py-[16px]">
                        <button
                            className="w-full py-[12px] border border-[#ddd] rounded-[12px] text-[#999] text-[18px] leading-[28px]"
                            onClick={() => navigate("/orders")}
                        >
                            목록으로
                        </button>
                    </div>
                )}

                <div
                    className={`${viewNote ? "opacity-1 pointer-events-auto" : "opacity-0 pointer-events-none"} flex items-end fixed left-0 right-0 bottom-0 top-0 bg-[rgba(0,0,0,0.5)] transition-all duration-[0.5s]`}
                >
                    <div
                        className={`${viewNote ? "transform-none" : "translate-y-full"} wrapper transition-all duration-[0.5s] w-full max-w-[768px] bg-white mx-auto rounded-[15px_15px_0_0] py-[24px]`}
                    >
                        <p className="text-[20px] leading-[30px]">주문취소 유의사항</p>
                        <CancelNote className="mb-[40px]" />
                        <button
                            type="button"
                            className="bg-[#273219] text-white text-[18px] font-bold rounded-[12px] p-[12px] text-center w-full"
                            onClick={() => setViewNote(false)}
                        >
                            확인
                        </button>
                    </div>
                </div>

                {cancel && (
                    <div className="absolute inset-0 bg-white">
                        <Header goBack onClickGoBack={() => setCancel(false)} title="주문취소" />
                        <div className="wrapper py-[16px]">
                            <p className="text-[24px] leading-[36px]">
                                <span className="text-[#4B632B]">미확인 건만</span> 취소가 가능합니다
                            </p>
                            <SimpleOrderItem
                                className="mt-[16px]"
                                category={product_info?.product_category}
                                name={product_info?.product_name}
                                price={product_info?.product_amount}
                                img={
                                    product_info?.product_image?.file_name
                                        ? `${enum_api_uri.api_uri}/${product_info.product_image.file_name}`
                                        : ""
                                }
                            />
                        </div>
                        <Formik
                            initialValues={initialValues}
                            // validateOnMount
                            validate={values => {
                                const errors: Record<string, string> = {};
                                if (!values.reason_idx) {
                                    errors.reason_idx = "취소사유를 선택해주세요.";
                                }
                                if (!values.agree) {
                                    errors.agree = "내용확인을 해주세요.";
                                }

                                return errors;
                            }}
                            onSubmit={(values, { setSubmitting }) => {
                                setSubmitting(true);
                                // onSubmit();
                                // console.log(values);
                                cancelSheetTriggerRef.current?.click();
                            }}
                        >
                            {({ isSubmitting, errors, values, setFieldValue, setValues, touched }) => {
                                const errorKeys = Object.keys(errors);
                                const isInValid = errorKeys.length > 0;
                                // console.log(values);

                                const handleOrderCancel = () => {
                                    cancelMutate(
                                        {
                                            order_idx: [Number(orderIdx)],
                                            cancel_reason_idx: Number(values.reason_idx),
                                            cancel_reason: values.reason_detail,
                                        },
                                        {
                                            onSuccess: () => {
                                                setCancel(false);
                                            },
                                            onError: (error: any) => {
                                                const err_msg = errorMsgHandler(error);
                                                openOverlay(<SystemPopup message={err_msg} onClickClose={closeOverlay} />);
                                            },
                                        },
                                    );
                                };

                                return (
                                    <Form>
                                        <div className="wrapper border-t-[8px] border-[#f3f3f8] py-[16px] flex flex-col gap-[16px]">
                                            <div>
                                                <label htmlFor="reason_idx" className="text-[#484964] inline-block mb-[4px]">
                                                    취소사유 <span className="text-[#4B632B] ml-[8px]">*</span>
                                                </label>

                                                <Sheet>
                                                    <SheetTrigger asChild className="w-full" ref={sheetTriggerRef}>
                                                        <div
                                                            className={`overflow-hidden flex items-center justify-between rounded-xl border bg-white pr-[20px] ${touched.reason_idx && errorKeys.includes("reason_idx") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                        >
                                                            <Field
                                                                name="reason_idx"
                                                                id="reason_idx"
                                                                value={
                                                                    values.reason_idx
                                                                        ? cancelData?.data.find(
                                                                              data => data.cancel_reason_idx === values.reason_idx,
                                                                          )?.cancel_reason
                                                                        : ""
                                                                }
                                                                readOnly
                                                                placeholder="취소사유를 선택하세요"
                                                                className="cursor-pointer p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                            />
                                                            <SelectIcon />
                                                        </div>
                                                    </SheetTrigger>
                                                    <SheetContent
                                                        side="bottom"
                                                        className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                    >
                                                        <SheetHeader>
                                                            <SheetTitle className="font-[500] text-[20px] leading-[30px] text-left">
                                                                취소사유
                                                            </SheetTitle>
                                                            <SheetDescription className="flex flex-col">
                                                                {cancelData?.data.map(data => {
                                                                    return (
                                                                        <span
                                                                            key={data.cancel_reason_idx}
                                                                            className={`cursor-default py-[12px] rounded-[12px] ${values.reason_idx === data.cancel_reason_idx ? "bg-[#F2F6EF] text-[#4B632B] text-[18px] leading-[28px] font-bold" : "text-[#484964] text-[20px] leading-[30px] font-[500]"}`}
                                                                            onClick={() => {
                                                                                setFieldValue("reason_idx", data.cancel_reason_idx);
                                                                                sheetTriggerRef.current?.click();
                                                                            }}
                                                                        >
                                                                            {data.cancel_reason}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </SheetDescription>
                                                        </SheetHeader>
                                                    </SheetContent>
                                                </Sheet>
                                                <ErrorMessage
                                                    name="reason_idx"
                                                    render={message => (
                                                        <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">{message}</div>
                                                    )}
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="reason_detail" className="text-[#484964] inline-block mb-[4px]">
                                                    상세사유
                                                </label>
                                                <Field name="reason_detail">
                                                    {({ field }: FieldProps) => {
                                                        return (
                                                            <TextareaField
                                                                name={field.name}
                                                                value={field.value}
                                                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                                                    const value = event.target.value;
                                                                    setFieldValue(field.name, value);
                                                                }}
                                                                placeholder="상세사유를 입력해주세요.(100자)"
                                                                textMaxLength={100}
                                                            />
                                                        );
                                                    }}
                                                </Field>
                                            </div>
                                        </div>
                                        <div className="wrapper border-t-[8px] border-[#f3f3f8] py-[16px]">
                                            <p>꼭 확인해 주세요.</p>
                                            <CancelNote className="mb-[8px]" />
                                            <div className="mb-[24px] flex gap-[8px] items-center font-[500]">
                                                <Field name="agree">
                                                    {({ field }: FieldProps) => {
                                                        return (
                                                            <Checkbox
                                                                type="square"
                                                                id="agree"
                                                                checked={field.value}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                                    const checked = event.target.checked;
                                                                    setFieldValue(field.name, checked);
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                </Field>

                                                <label htmlFor="agree">위 내용을 확인했어요.</label>
                                            </div>

                                            <button
                                                type="submit"
                                                data-disabled={isInValid}
                                                className="text-[#484964] border border-[#ddd] w-full rounded-[12px] py-[12px] data-[disabled='true']:opacity-[0.4]"
                                            >
                                                주문취소
                                            </button>

                                            <Sheet>
                                                <SheetTrigger className="absolute" ref={cancelSheetTriggerRef} />
                                                <SheetContent
                                                    side="bottom"
                                                    className="bg-white max-w-[768px] mx-auto rounded-[15px_15px_0_0]"
                                                >
                                                    <SheetHeader>
                                                        <SheetTitle className="text-[20px] leading-[30px] text-left font-[500]">
                                                            주문내역을 취소하시겠어요?
                                                        </SheetTitle>
                                                        <div>
                                                            <SheetDescription className="font-[400] text-[#484964] text-left">
                                                                취소한 주문내역은 다시 복구가 불가합니다. <br />
                                                                동일한 내용으로 다시 신청해 주세요.
                                                            </SheetDescription>
                                                            <div className="flex gap-[8px] mt-[40px]">
                                                                <button
                                                                    type="button"
                                                                    className="flex-1 border border-[#dddddd] text-[18px] leading-[28px] font-[500] text-[#484964] rounded-[12px] py-[12px]"
                                                                    onClick={() => {
                                                                        cancelSheetTriggerRef.current?.click();
                                                                    }}
                                                                >
                                                                    나가기
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="flex-1 border border-[#273219] text-[18px] leading-[28px] font-bold text-white rounded-[12px] py-[12px] bg-[#273219]"
                                                                    onClick={() => {
                                                                        handleOrderCancel();
                                                                        cancelSheetTriggerRef.current?.click();
                                                                    }}
                                                                >
                                                                    주문신청 취소
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </SheetHeader>
                                                </SheetContent>
                                            </Sheet>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                )}
            </div>
        </OverlayWrapper>
    );
}
